import { LitElement, html } from "lit";

import { customElement, property } from "lit/decorators.js";

import { appContext } from "ui-common/appContext.ts";
import { DataQueryController } from "ui-common";

import { styles } from "./app-menu.css";
import { DESKTOP_MENU_ITEMS, FAN_MENU_ITEMS } from "./menu-items";
import type { MenuItem } from "./types";

import { type Theme, ThemeController } from "../../../../../libs/ui-common/theme";
import type { RoutePath } from "../../router";
import { navigateTo } from "../../router";
import { t } from "../../translations/i18n";
import { featureFlag } from "ui-components/wow-feature/wow-feature";
import { featureFlagsQueryOptions } from "../../queries/feature-flags";
import "ui-components/tab-menu";

type CombinedRoutePath = RoutePath | "feed" | "explore";

@customElement("app-menu")
export class AppMenu extends LitElement {
  static override styles = styles;

  private themeController = new ThemeController(this);
  private featureFlagsQuery = new DataQueryController(this, () => featureFlagsQueryOptions);

  @property({ type: String })
  private _currentPath?: CombinedRoutePath;

  override connectedCallback() {
    super.connectedCallback();

    this.setCurrentPathFromUrl();
    // Listen for route changes to update active state
    window.addEventListener("popstate", this.handleRouteChange);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("popstate", this.handleRouteChange);
  }

  private setCurrentPathFromUrl() {
    // Extract the route path, removing the app prefix (backoffice)
    const appPath = window.location.pathname.substring(1).split("/").slice(1).join("/");
    this._currentPath = appPath as RoutePath;
  }

  private handleRouteChange = () => {
    this.setCurrentPathFromUrl();
    this.requestUpdate();
  };

  private _isActive(path: CombinedRoutePath): boolean {
    if (this._currentPath === path) {
      return true;
    }

    // Auto-detect if current path is a subpage of this menu item
    // e.g., "posts/worlds" should activate "posts" menu item
    return this._currentPath?.startsWith(path + "/") || false;
  }

  private _handleThemeToggle(theme: Theme) {
    this.themeController.setTheme(theme);
  }

  private _renderMenuSection(items: MenuItem[]) {
    return html`
      <div class="menu-section">
        ${items.map(
          (item) => html`
            <a
              href="/${item.path}"
              class="menu-item ${this._isActive(item.path) ? "active" : ""}"
              @click=${this.getItemClickHandler(item)}
            >
              <span class="icon">${item.icon}</span>
              <span>${item.label}</span>
            </a>
          `,
        )}
      </div>
    `;
  }

  private _renderMenuItem(item: MenuItem) {
    return html`
      <a
        href="/${item.path}"
        class="menu-item ${this._isActive(item.path) ? "active" : ""}"
        @click=${this.getItemClickHandler(item)}
      >
        <span class="icon">${item.icon}</span>
        <span>${item.label}</span>
      </a>
    `;
  }

  getItemClickHandler(item: MenuItem) {
    return async (e: MouseEvent) => {
      e.preventDefault();

      if (["feed", "explore"].includes(item.path)) {
        return window.open(`/${item.path}`, "_self", "noopener,noreferrer");
      }

      const path = item.path as RoutePath;
      const success = await navigateTo(path);

      if (!success) return;

      this._currentPath = item.path;
    };
  }

  override render() {
    const userHandle = appContext.currentUser?.handle;
    const userAvatar = appContext.currentUser?.avatarUrl;

    const isCreator = !!userHandle && !!userAvatar;

    const isFan = !!appContext.currentUser?.id && !isCreator;

    const featureFlags = this.featureFlagsQuery.result?.data?.features ?? [];
    const hasLiveEvents = featureFlags.includes("paid-live-events") || featureFlags.includes("open-live-events");

    // Filter menu items based on feature flags
    const filteredDesktopMenuItems = DESKTOP_MENU_ITEMS.filter((item) => {
      if (item.path === "live-events") {
        return hasLiveEvents;
      }
      return true;
    });

    return html`
      <section class="layout">
        <div class="logo">
          <app-logo class="show-on-desktop"></app-logo>
        </div>
        ${featureFlag(
          isCreator,
          html`<nav class="menu-list menu-list-desktop">${this._renderMenuSection(filteredDesktopMenuItems)}</nav>
            <tab-menu
              class="menu-list-mobile"
              .createManageOptions=${{
                header: t("creator-modal.manageCreate"),
                links: [
                  {
                    label: t("creator-modal.editProfile"),
                    description: t("creator-modal.profileDescription"),
                    href: "/my/profile",
                    icon: html`<ph-user size="22" slot="icon"></ph-user>`,
                  },
                  {
                    label: t("creator-modal.editLinks"),
                    description: t("creator-modal.linksDescription"),
                    href: "/my/links",
                    icon: html`<ph-link size="22" slot="icon"></ph-link>`,
                  },
                  {
                    label: t("creator-modal.editWorlds"),
                    description: t("creator-modal.worldsDescription"),
                    href: "/my/worlds",
                    icon: html`<ph-images size="22" slot="icon"></ph-images>`,
                  },
                  ...(hasLiveEvents
                    ? [
                        {
                          label: t("creator-modal.editLiveEvents"),
                          description: t("creator-modal.liveEventsDescription"),
                          href: "/my/live-events",
                          icon: html`<ph-video-camera size="22" slot="icon"></ph-video-camera>`,
                        },
                      ]
                    : []),
                ],
              }}
              .labels=${{ feed: t("menu.feed"), explore: t("menu.explore") }}
            ></tab-menu>`,
        )}
        ${featureFlag(
          isFan,
          html`<nav class="menu-list menu-list-desktop">${this._renderMenuSection(FAN_MENU_ITEMS)}</nav>
            <tab-menu
              class="menu-list-mobile"
              .createManageOptions=${{
                header: t("creator-modal.manageCreate"),
                links: [
                  {
                    label: t("creator-modal.editProfile"),
                    description: t("creator-modal.profileDescription"),
                    href: "/my/profile",
                    icon: html`<ph-user size="22" slot="icon"></ph-user>`,
                  },
                  {
                    label: t("creator-modal.editLinks"),
                    description: t("creator-modal.linksDescription"),
                    href: "/my/links",
                    icon: html`<ph-link size="22" slot="icon"></ph-link>`,
                  },
                  {
                    label: t("creator-modal.editWorlds"),
                    description: t("creator-modal.worldsDescription"),
                    href: "/my/worlds",
                    icon: html`<ph-images size="22" slot="icon"></ph-images>`,
                  },
                ],
              }}
              .labels=${{ feed: t("menu.feed"), explore: t("menu.explore") }}
            ></tab-menu>`,
        )}
        <div class="bottom-section">
          <wow-button @click=${() => appContext.signOut()} variant="subtle"
            ><ph-sign-out weight="bold" size="20"></ph-sign-out>${t("menu.signOut")}</wow-button
          >

          <div class="theme-toggles">
            <button
              class="theme-toggle ${this.themeController.resolvedTheme === "light" ? "active" : ""}"
              title=${t("theme.lightTheme")}
              @click=${() => this._handleThemeToggle("light")}
            >
              <span class="icon"><ph-sun weight="bold"></ph-sun></span>
            </button>
            <button
              class="theme-toggle ${this.themeController.resolvedTheme === "dark" ? "active" : ""}"
              title=${t("theme.darkTheme")}
              @click=${() => this._handleThemeToggle("dark")}
            >
              <span class="icon"><ph-moon weight="bold"></ph-moon></span>
            </button>
          </div>
        </div>
      </section>
    `;
  }
}
